import { applicationApiService } from '../services/applicationApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationState,
  ApplicationDraft,
} from '../types/application';
import { useState } from 'react';
import {
  OfferDerogationReason,
  type CreditProductType,
  type OceanCreditProductName,
} from '@/features/offer/types';
import type { ContractResponse } from '@/features/loan/types/loan';
import { loanApiService } from '@/features/loan/services';
import { sessionStorageUtils } from '@/lib/storageUtils';

export const ApplicationQueryKeys = {
  applicationState: ['application-state'],
  installmentDraft: ['installment-draft'],
  creditLineDraft: ['credit-line-draft'],
};

// Persist the amount / number-of-rates selection per credit type so it survives
// a page refresh within the same session (the drafts otherwise live only in the
// in-memory React Query cache).
const CALCULATOR_STORAGE_KEYS = {
  installment: 'calculator:installment-selection',
  creditLine: 'calculator:credit-line-selection',
} as const;

type StoredSelection = {
  amount?: number;
  numberOfRates?: number;
  bankCardId?: string;
};

const readStoredSelection = (key: string): ApplicationDraft => {
  const saved = sessionStorageUtils.get<StoredSelection>(key);
  return saved
    ? {
        amount: saved.amount,
        numberOfRates: saved.numberOfRates,
        bankCardId: saved.bankCardId,
      }
    : ({} as ApplicationDraft);
};

const persistStoredSelection = (key: string, draft: ApplicationDraft) => {
  sessionStorageUtils.set<StoredSelection>(key, {
    amount: draft.amount,
    numberOfRates: draft.numberOfRates,
    bankCardId: draft.bankCardId,
  });
};

interface UseApplicationReturn {
  applicationState: ApplicationState | undefined;
  isApplicationStateLoading: boolean;
  installmentDraft: ApplicationDraft;
  creditLineDraft: ApplicationDraft;
  setInstallmentAmount: (amount: number) => void;
  setCreditLineAmount: (amount: number) => void;
  setCreditLinePayDate: (payDate: Date) => void;
  setInstallmentNumberOfRates: (numberOfRates: number) => void;
  setInstallmentIsDerogation: (isDerogation?: boolean) => void;
  setInstallmentBankCardId: (bankCardId: string | undefined) => void;
  applyOffer: (
    offerId: string,
    offerType: CreditProductType,
    creditProductName: OceanCreditProductName,
    amount: number,
    numberOfRates?: number,
    firstBillDate?: Date,
    isDerogation?: boolean,
    isRealBeneficiary?: boolean
  ) => Promise<ContractResponse | null>;
  isApplyingOffer: boolean;
  applyToOfferError: Error | null;
  getApplicationState: () => Promise<ApplicationState | undefined>;
}

export function useApplication(
  fetchApplicationState: boolean = false
): UseApplicationReturn {
  const [isApplyingOffer, setIsApplyingOffer] = useState(false);

  const queryClient = useQueryClient();

  const applicationStateQuery = useQuery({
    queryKey: ApplicationQueryKeys.applicationState,
    queryFn: () => applicationApiService.getLoanApplicationState(),
    enabled: fetchApplicationState,
  });

  const getApplicationState = async () => {
    const result = await applicationStateQuery.refetch();
    return result.data;
  };

  const initialApplicationDraft = (): ApplicationDraft => {
    return {} as ApplicationDraft;
  };

  const installmentDraftQuery = useQuery({
    queryKey: ApplicationQueryKeys.installmentDraft,
    queryFn: initialApplicationDraft,
    initialData: () => readStoredSelection(CALCULATOR_STORAGE_KEYS.installment),
    enabled: false,
    staleTime: Infinity,
  });

  const creditLineDraftQuery = useQuery({
    queryKey: ApplicationQueryKeys.creditLineDraft,
    queryFn: initialApplicationDraft,
    initialData: () => readStoredSelection(CALCULATOR_STORAGE_KEYS.creditLine),
    enabled: false,
    staleTime: Infinity,
  });

  const setCreditLineAmount = (amount: number) => {
    const next = queryClient.setQueryData<ApplicationDraft>(
      ApplicationQueryKeys.creditLineDraft,
      (prev) => ({
        ...prev,
        amount: amount,
      })
    );
    if (next) persistStoredSelection(CALCULATOR_STORAGE_KEYS.creditLine, next);
  };

  const setInstallmentAmount = (amount: number) => {
    const next = queryClient.setQueryData<ApplicationDraft>(
      ApplicationQueryKeys.installmentDraft,
      (prev) => ({
        ...prev,
        amount: amount,
      })
    );
    if (next) persistStoredSelection(CALCULATOR_STORAGE_KEYS.installment, next);
  };

  const setCreditLinePayDate = (payDate: Date) => {
    queryClient.setQueryData(
      ApplicationQueryKeys.creditLineDraft,
      (prev: ApplicationDraft) => ({
        ...prev,
        firstBillDate: payDate,
      })
    );
  };

  const setInstallmentNumberOfRates = (numberOfRates: number) => {
    const next = queryClient.setQueryData<ApplicationDraft>(
      ApplicationQueryKeys.installmentDraft,
      (prev) => ({
        ...prev,
        numberOfRates: numberOfRates,
      })
    );
    if (next) persistStoredSelection(CALCULATOR_STORAGE_KEYS.installment, next);
  };
  // The card the installment credit is paid to; none means the main card.
  const setInstallmentBankCardId = (bankCardId: string | undefined) => {
    const next = queryClient.setQueryData<ApplicationDraft>(
      ApplicationQueryKeys.installmentDraft,
      (prev) => ({
        ...prev,
        bankCardId: bankCardId,
      })
    );
    if (next) persistStoredSelection(CALCULATOR_STORAGE_KEYS.installment, next);
  };

  const setInstallmentIsDerogation = (isDerogation?: boolean) => {
    queryClient.setQueryData(
      ApplicationQueryKeys.installmentDraft,
      (prev: ApplicationDraft) => ({
        ...prev,
        isDerogation: isDerogation,
      })
    );
  };

  const applyOfferMutation = useMutation({
    mutationFn: (applicationDraft: ApplicationDraft) =>
      applicationApiService.applyOffer(applicationDraft),
  });

  const generateContractMutation = useMutation({
    mutationFn: ({ isRealBeneficiary }: { isRealBeneficiary: boolean }) =>
      loanApiService.generateContract(isRealBeneficiary),
  });

  const applyOffer = async (
    offerId: string,
    offerType: CreditProductType,
    creditProductName: OceanCreditProductName,
    amount: number,
    numberOfRates?: number,
    firstBillDate?: Date,
    isDerogation?: boolean,
    // Not politically exposed and the real beneficiary of the money.
    isRealBeneficiary?: boolean
  ): Promise<ContractResponse | null> => {
    setIsApplyingOffer(true);
    let applicationDraft: ApplicationDraft;
    if (
      creditProductName === 'DolphinPlus' ||
      creditProductName === 'Dolphin'
    ) {
      applicationDraft = {
        ...creditLineDraftQuery.data,
        creditProductName: creditProductName,
        offerId: offerId,
        creditProductType: offerType,
        amount: amount,
        firstBillDate: firstBillDate,
        offerDerogationReasonId: isDerogation
          ? OfferDerogationReason.DegreeOfIndebtedness
          : undefined,
      };
    } else {
      applicationDraft = {
        ...installmentDraftQuery.data,
        creditProductName: creditProductName,
        offerId: offerId,
        creditProductType: offerType,
        amount: amount,
        numberOfRates: numberOfRates,
        lenderTermsAccepted: true,
        offerDerogationReasonId: isDerogation
          ? OfferDerogationReason.DegreeOfIndebtedness
          : undefined,
      };
    }

    try {
      await applyOfferMutation.mutateAsync(applicationDraft);
      const contract = await generateContractMutation.mutateAsync({
        isRealBeneficiary:
          isRealBeneficiary ?? applicationDraft.isRealBeneficiary ?? false,
      });
      return contract;
    } finally {
      setIsApplyingOffer(false);
    }
  };

  return {
    applicationState: applicationStateQuery.data,
    isApplicationStateLoading: applicationStateQuery.isLoading,
    installmentDraft: installmentDraftQuery.data,
    creditLineDraft: creditLineDraftQuery.data,
    setInstallmentAmount,
    setCreditLineAmount,
    setCreditLinePayDate,
    setInstallmentNumberOfRates,
    setInstallmentIsDerogation,
    setInstallmentBankCardId,
    applyOffer,
    isApplyingOffer,
    applyToOfferError:
      generateContractMutation.error || applyOfferMutation.error,
    getApplicationState,
  };
}
